import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { IonButtons, IonContent, IonIcon, IonPage, isPlatform, useIonViewDidEnter } from '@ionic/react';
import { RouteComponentProps } from 'react-router';
import { AppHeader, Container, CustomButton } from '@mindly/ui-components';

import Logo from '../../../../images/header_logo.svg?react';
import { OnBoardingWrapper } from './OnBoardingMain.style';
import { useSelector } from 'react-redux';
import {
  getMainPageButtonsData,
  getMainPageData,
  getNextStep,
  getOnBoardingID,
  getOnBoardingLocale,
  getOnBoardingMainPageSettings,
  getOnBoardingName,
  isLoading,
} from '../../../../redux/onBoarding/selector';
import useOnBoardingNextStep from '../../../../hooks/useOnBoardingNextStep';
import { ImpactStyle } from '@capacitor/haptics';
import OnBoardingLoader from '../../components/OnBoardingLoader';
import { closeOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { userSelector } from '../../../../redux/user/selector';
import { FunnelTypeEnum, useAnalytics, useAuthFlow } from '../../../../shared';
import { uid } from 'uid';
import { setSplashColor } from 'src/shared/utils/splashColor';
import { globalVariables } from 'src/redux/firestoreGlobalVariables/selector';
import { useTranslation } from 'react-i18next';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';

interface OnBoardingMainProps extends RouteComponentProps<{ id: string }> {}

const OnBoardingMain: FC<OnBoardingMainProps> = () => {
  const history = useHistory();
  const { authUser } = useAuthFlow();
  const { i18n } = useTranslation();

  const mainWrapperRef = useRef<HTMLDivElement | null>(null);

  const nextStep = useSelector(getNextStep);
  const mainPageData = useSelector(getMainPageData);
  const onBoardingName = useSelector(getOnBoardingName);
  const buttonsData = useSelector(getMainPageButtonsData);
  const isLoadingSelector = useSelector(isLoading);
  const mainPageSettings = useSelector(getOnBoardingMainPageSettings);
  const onBoardingId = useSelector(getOnBoardingID);
  const currentUser = useSelector(userSelector);
  const globalVars = useSelector(globalVariables);
  const onBoardingLocale = useSelector(getOnBoardingLocale);

  const [isPageView, setIsPageView] = useState(false);

  const nextStepAction = useOnBoardingNextStep();

  const { trackPredefinedEvent } = useAnalytics();

  useIonViewDidEnter(async () => {
    setSplashColor('#fffffe');

    setIsPageView(true);
  }, [onBoardingName]);

  useEffect(() => {
    if (!onBoardingName) return;
  }, [onBoardingName]);

  useEffect(() => {
    if (!mainWrapperRef.current) return;
    if (isPlatform('desktop')) return;
    if (isLoadingSelector) return;

    const pElements = mainWrapperRef.current?.querySelectorAll('p');

    if (pElements.length) {
      pElements.forEach((el) => {
        if (el.firstElementChild?.tagName === 'IMG') {
          el.classList.add('full-width');
        }
      });
    }
  }, [mainWrapperRef, isLoadingSelector]);

  const nextStepHandler = useCallback(async () => {
    setIsPageView(false);
    await nextStepAction();
    if (!authUser) {
      trackPredefinedEvent('ViewContent', {
        eventInstanceId: uid(16),
      });
    }
  }, [nextStepAction, authUser]);

  const handleAnalyticEvent = () => {
    if (!onBoardingId) return;

    trackPredefinedEvent('FUNNEL_STARTED', {
      funnel_id: onBoardingId,
      funnel_name: onBoardingName,
      funnel_type:
        onBoardingId === globalVars?.defaultOnBoardingId?.[i18n.language]
          ? FunnelTypeEnum.DEFAULT
          : FunnelTypeEnum.WEB_TO_APP,
      screen_id: 'main screen',
      screen_name: 'main screen',
      locale: onBoardingLocale,
    });
  };

  useEffect(() => {
    if (!isPageView) return;
    if (isLoadingSelector) return;
    if (!onBoardingId || !onBoardingName) return;

    handleAnalyticEvent();
  }, [isPageView, isLoadingSelector, onBoardingId, onBoardingName]);

  useEffect(() => {
    if (!isPageView) return;
    if (isLoadingSelector) return;
    if (!onBoardingId) return;

    if (!mainPageData) {
      nextStepHandler();
    }
  }, [isPageView, isLoadingSelector, mainPageData, onBoardingId, nextStepHandler]);

  return (
    <IonPage>
      <AppHeader>
        {!isLoadingSelector && mainPageSettings.show_close_button !== true && (
          <IonButtons slot="start">
            <Logo />
          </IonButtons>
        )}
        {!!currentUser?.id && (
          <IonButtons slot={'end'}>
            <IonIcon
              icon={closeOutline}
              style={{
                fontSize: '27px',
                color: '#1D201F',
                cursor: 'pointer',
              }}
              onClick={() => history.back()}
            />
          </IonButtons>
        )}
      </AppHeader>
      <IonContent className={'white'}>
        <Container
          style={{
            justifyContent: isLoadingSelector ? 'center' : 'flex-start',
          }}
        >
          <OnBoardingWrapper ref={mainWrapperRef}>
            {isLoadingSelector ? (
              <OnBoardingLoader />
            ) : (
              <>
                <div dangerouslySetInnerHTML={{ __html: mainPageData }} />
                {nextStep?.id && (
                  <div className={`next-buttons-wrapper ${buttonsData.show_additional_button ? 'additional' : ''}`}>
                    <CustomButton
                      type="button"
                      buttonType="primary"
                      onClick={async () => {
                        isomorficHapticsImpact({ style: ImpactStyle.Light });
                        await nextStepHandler();
                      }}
                      className="start-btn"
                    >
                      {buttonsData.button_label}
                    </CustomButton>
                    {buttonsData.show_additional_button && (
                      <CustomButton
                        type="button"
                        buttonType="primary"
                        onClick={async () => {
                          isomorficHapticsImpact({ style: ImpactStyle.Light });
                          await nextStepHandler();
                        }}
                        className="start-btn"
                      >
                        {buttonsData.additional_button_label}
                      </CustomButton>
                    )}
                  </div>
                )}
              </>
            )}
          </OnBoardingWrapper>
        </Container>
      </IonContent>
    </IonPage>
  );
};

export default OnBoardingMain;
